import { create } from 'zustand';
import { WorkReponse } from '../../../model/work_response';
import { createDI } from '../../../context/di/di';
import { ApiError, useApi } from '../../../api/api';
import { IncidentListReponse } from '../../../model/incident_response';
import { CompanyListResponse } from '../../../model/company_response';
import { IssueRequest } from '../../../model/issues_request';

/**
 * 臨時作業入力画面 アクションを管理するための DI コンテキスト(アクション)
 */
export type IssueAction = {
  /**
   * 作業情報取得
   * @param workId
   * @param planId
   */
  getTaskDetails: (workId: number, planId: number) => Promise<WorkReponse>;

  /**
   * 発生事象一覧取得
   * @param businessType
   */
  getIncidents: (businessType: string) => Promise<IncidentListReponse>;

  /**
   * 会社一覧取得
   * @param businessType
   */
  getCompanies: (businessType: string) => Promise<CompanyListResponse>;

  /**
   * 臨時作業入力登録
   * @param item
   */
  registeIssue: (item: IssueRequest) => Promise<void>;

  /**
   * 臨時作業入力更新
   * @param item
   * @param issueId
   * @param etag
   * @param isForceUpd
   */
  updateIssue: (item: IssueRequest, issueId: number, etag: string, isForceUpd?: boolean) => Promise<void>;
};

/**
 * 臨時作業入力画面 状態とアクションを管理するための DI コンテキストを作成
 */
const [Provider, use] = createDI<IssueAction>();
export { Provider as IssueProvider, use as useIssueStore };

/**
 * 臨時作業入力画面 状態とアクション
 */
export const defaultIssueStore = () => {
  const api = useApi();

  // @ts-ignore
  return create<IssueAction>(() => {
    return {
      getTaskDetails: async (workId: number, planId: number) => {
        const resp = await api.path(`/api/works/${workId}/plans/${planId}?type=request`).get();
        if (resp.isError) {
          throw new ApiError(resp);
        }
        return resp.data as WorkReponse;
      },
      getIncidents: async (businessType: string) => {
        const resp = await api.path(`/api/issues/incidents/${businessType}`).get();
        if (resp.isError) {
          throw new ApiError(resp);
        }
        return resp.data as IncidentListReponse;
      },
      getCompanies: async (businessType: string) => {
        const resp = await api.path(`/api/resources/companies/${businessType}`).get();
        if (resp.isError) {
          throw new ApiError(resp);
        }
        return resp.data as IncidentListReponse;
      },
      registeIssue: async (item: IssueRequest) => {
        const resp = await api.path('/api/issues').post(item);
        if (resp.isError) {
          throw new ApiError(resp);
        }
      },
      updateIssue: async (item: IssueRequest, issueId: number, etag: string, isForceUpd: boolean = false) => {
        const resp = await api.path(`/api/issues/${issueId}`)
          .header({ 'If-Match': etag, 'X-Force-Update': String(isForceUpd) })
          .put(item);
        if (resp.isError) {
          throw new ApiError(resp);
        }
      },
    };
  });
};
